import dayjs from 'dayjs';
import { REPORT_STATUS } from '@/constants/report';
import type { ReportComment, ReportRecord, ReportStatus } from '@/types/report';

export type EmployeeTodayStatus = 'not_submitted' | ReportStatus;

export interface EmployeeTodayStatusMeta {
  key: EmployeeTodayStatus;
  label: string;
  colorClass: string;
}

export function resolveTodayStatus(report: ReportRecord | null): EmployeeTodayStatus {
  if (!report || report.status === REPORT_STATUS.DRAFT) {
    return 'not_submitted';
  }

  return report.status;
}

export function getTodayStatusMeta(status: EmployeeTodayStatus): EmployeeTodayStatusMeta {
  switch (status) {
    case REPORT_STATUS.SUBMITTED:
      return { key: status, label: '已提交', colorClass: 'mobile-status-badge--submitted' };
    case REPORT_STATUS.REVIEWED:
      return { key: status, label: '已读', colorClass: 'mobile-status-badge--reviewed' };
    case REPORT_STATUS.COMMENTED:
      return { key: status, label: '已评论', colorClass: 'mobile-status-badge--commented' };
    default:
      return { key: 'not_submitted', label: '未提交', colorClass: 'mobile-status-badge--pending' };
  }
}

export function shouldShowWriteAction(status: EmployeeTodayStatus): boolean {
  return status === 'not_submitted';
}

export interface LatestLeaderComment {
  leaderName: string;
  createdAt: string;
  content: string;
}

export function findLatestLeaderComment(
  reports: ReportRecord[],
  employeeId: string,
  leaderNameById: Map<string, string>,
): LatestLeaderComment | null {
  const allComments: Array<ReportComment & { report: ReportRecord }> = [];

  for (const report of reports) {
    for (const comment of report.comments) {
      if (comment.receiverId === employeeId) {
        allComments.push({ ...comment, report });
      }
    }
  }

  if (allComments.length === 0) {
    return null;
  }

  allComments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const latest = allComments[0];
  const leaderName = leaderNameById.get(latest.authorId) ?? '领导';

  return {
    leaderName,
    createdAt: latest.createdAt,
    content: latest.content,
  };
}

export interface EmployeePersonalStats {
  consecutiveDays: number;
  monthSubmitCount: number;
  pendingSubmitCount: number;
}

function toDateKey(value: string | Date): string {
  return dayjs(value).format('YYYY-MM-DD');
}

export function computeEmployeePersonalStats(
  reports: ReportRecord[],
  todayKey: string,
): EmployeePersonalStats {
  const submittedByDate = new Map<string, ReportRecord>();

  for (const report of reports) {
    if (report.status === REPORT_STATUS.DRAFT) {
      continue;
    }
    submittedByDate.set(toDateKey(report.reportDate), report);
  }

  let consecutiveDays = 0;
  let cursor = dayjs(todayKey);
  while (submittedByDate.has(cursor.format('YYYY-MM-DD'))) {
    consecutiveDays += 1;
    cursor = cursor.subtract(1, 'day');
  }

  const monthStart = dayjs(todayKey).startOf('month');
  const monthEnd = dayjs(todayKey).endOf('day');

  let monthSubmitCount = 0;
  let pendingSubmitCount = 0;

  let day = monthStart;
  while (day.isBefore(monthEnd) || day.isSame(monthEnd, 'day')) {
    const key = day.format('YYYY-MM-DD');
    if (submittedByDate.has(key)) {
      monthSubmitCount += 1;
    } else if (day.isBefore(dayjs(todayKey)) || day.isSame(dayjs(todayKey), 'day')) {
      pendingSubmitCount += 1;
    }
    day = day.add(1, 'day');
  }

  return {
    consecutiveDays,
    monthSubmitCount,
    pendingSubmitCount,
  };
}
